import type { CardId, WallCard } from '@walls/store';
import { getCardRect } from './cardRects';
import type { RelativeCard, RelativeCardDirection } from './types';

const alignmentTolerance = 2;
const adjacentGap = 6;

export function getRelativeCards(
  cards: WallCard[],
  card: WallCard,
  trackedIds: CardId[] = [card.id]
): RelativeCard[] {
  const trackedCards: RelativeCard[] = [];
  const untrackedCards = getAdjacentCards(cards, card, trackedIds);

  for (const relativeCard of untrackedCards) {
    trackedCards.push(relativeCard);
    trackedCards.push(...getRelativeCards(cards, relativeCard.card, trackedIds));
  }

  return trackedCards;
}

function getAdjacentCards(cards: WallCard[], card: WallCard, trackedIds: CardId[]) {
  const relatives: RelativeCard[] = [];
  const currentRect = getCardRect(card);

  if (!currentRect) {
    return relatives;
  }

  relatives.push(
    ...collectAlongAxis(cards, card, currentRect, trackedIds, 'vertical', 's'),
    ...collectAlongAxis(cards, card, currentRect, trackedIds, 'vertical', 'n'),
    ...collectAlongAxis(cards, card, currentRect, trackedIds, 'horizontal', 'e'),
    ...collectAlongAxis(cards, card, currentRect, trackedIds, 'horizontal', 'w')
  );

  return relatives;
}

function collectAlongAxis(
  cards: WallCard[],
  sourceCard: WallCard,
  sourceRect: { height: number; width: number; x: number; y: number },
  trackedIds: CardId[],
  axis: 'horizontal' | 'vertical',
  direction: RelativeCardDirection
) {
  const isVertical = axis === 'vertical';
  const candidates = cards
    .filter((card) => {
      const rect = getCardRect(card);

      if (!rect || card.id === sourceCard.id || card.isLocked) {
        return false;
      }

      const aligned = isVertical
        ? Math.floor(rect.x) > Math.floor(sourceRect.x) - alignmentTolerance &&
          Math.floor(rect.x) < Math.ceil(sourceRect.x) + alignmentTolerance
        : Math.floor(rect.y) > Math.floor(sourceRect.y) - alignmentTolerance &&
          Math.floor(rect.y) < Math.ceil(sourceRect.y) + alignmentTolerance;

      return aligned;
    })
    .sort((a, b) => {
      const aRect = getCardRect(a);
      const bRect = getCardRect(b);

      if (!aRect || !bRect) return 0;
      return isVertical ? aRect.y - bRect.y : aRect.x - bRect.x;
    });

  const orderedCandidates = direction === 'n' || direction === 'w' ? [...candidates].reverse() : candidates;
  const relatives: RelativeCard[] = [];
  let previousMatched: WallCard | null = null;

  for (const candidate of orderedCandidates) {
    const rect = getCardRect(candidate);
    const parentRect = previousMatched ? getCardRect(previousMatched) : sourceRect;

    if (!rect || !parentRect) {
      continue;
    }

    const isAfter =
      direction === 's'
        ? rect.y > sourceRect.y + sourceRect.height
        : direction === 'e'
          ? rect.x > sourceRect.x + sourceRect.width
          : direction === 'n'
            ? rect.y + rect.height < sourceRect.y
            : rect.x + rect.width < sourceRect.x;

    if (!isAfter) {
      continue;
    }

    const gap =
      direction === 's'
        ? rect.y - (parentRect.y + parentRect.height)
        : direction === 'e'
          ? rect.x - (parentRect.x + parentRect.width)
          : direction === 'n'
            ? parentRect.y - (rect.y + rect.height)
            : parentRect.x - (rect.x + rect.width);

    if (Math.floor(gap) > adjacentGap) {
      break;
    }

    if (!trackedIds.includes(candidate.id)) {
      trackedIds.push(candidate.id);
      relatives.push({
        card: candidate,
        parentId: previousMatched?.id ?? sourceCard.id,
        position: direction,
      });
    }

    previousMatched = candidate;
  }

  return relatives;
}
